import * as THREE from 'three';
import { Rng } from '../core/rng.js';
import { WeaponMaterials, ENV_OCCLUSION } from './materials.js';
import { Viewmodel } from './viewmodel.js';
import { ProjectileSim } from './ballistics.js';
import { THROWABLE_DEFS, buildRecoilPattern, SPREAD_MODS } from './defs.js';
import { WEAPON_DEFS, MODEL_LOADERS, STARTER_LOADOUT, LOADOUT_SLOTS } from './defs/index.js';
import { buildRifle } from './models/rifle.js';
import { buildPistol } from './models/pistol.js';
import { buildFists } from './models/fists.js';
import { buildKnife } from './models/knife.js';
import { clamp, clamp01, lerp, damp, DEG } from './mathx.js';

/** Everything unlocked from the start — there is no progression/unlock UI yet. */
export const STARTER_WEAPON_IDS = Object.freeze(
  Object.values(STARTER_LOADOUT).filter((id) => WEAPON_DEFS[id])
);
/** Four starter lanes used when the armory has not written a saved loadout. */
const DEFAULT_LOADOUT = STARTER_LOADOUT;

/**
 * The default loadout and melee, linked in rather than fetched.
 *
 * Every other weapon keeps the lazy `loadModel()` path — the rule in
 * defs/index.js still holds, and it is about not BUILDING fifty weapons'
 * geometry at boot, which these do not do: importing a builder costs parsing a
 * few KB, and the build only runs for what the player has equipped.
 *
 * What this buys is that the weapons a player always has cannot depend on a
 * runtime code-split fetch resolving. Observed in the field: all four of these
 * dynamic imports hung indefinitely in a browser where every chunk and every
 * preload dependency returned 200, which left `engine.init()` awaiting forever
 * — the game simply never started, with nothing on screen to say why.
 *
 * ~34 KB in the main bundle, against a boot that cannot be stalled by it.
 */
const STATIC_MODELS = { rifle: buildRifle, pistol: buildPistol, fists: buildFists, knife: buildKnife };

/**
 * How long a weapon's model chunk may take to arrive before boot gives up on it.
 *
 * Generous on purpose. This exists to break a request that has genuinely died,
 * NOT to police a slow one: these imports are issued while init() is saturating
 * the main thread, so module evaluation can lag well past what the network took.
 * Set to 10s it fired on healthy loads and dropped every weapon, which puts the
 * player in the world holding nothing but their own forearms.
 */
const MODEL_LOAD_TIMEOUT_MS = 45000;

/**
 * Fetch one weapon's model chunk, but never let it hold boot open.
 *
 * This dynamic import is the ONLY await inside any subsystem's init(), which
 * makes it the only thing that can stall `engine.init()` — and a stalled module
 * request neither rejects nor resolves, so there is nothing to catch. A request
 * that dies in flight (a retired chunk hash, a flaky connection) would
 * otherwise leave the whole engine half-booted with no error on screen.
 *
 * Resolves to null instead, and the caller skips that weapon.
 */
async function loadWeaponModel(id, failures, attempt = 0) {
  // Linked in: no fetch, no preload helper, nothing that can fail to settle.
  if (STATIC_MODELS[id]) return STATIC_MODELS[id];
  let timer;
  try {
    if (typeof MODEL_LOADERS[id] !== 'function') {
      throw new Error(`no loadModel registered (defs/${id}.js missing or not globbed)`);
    }
    const fn = await Promise.race([
      MODEL_LOADERS[id](),
      new Promise((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error(`timed out after ${MODEL_LOAD_TIMEOUT_MS}ms`)), MODEL_LOAD_TIMEOUT_MS);
      }),
    ]);
    // `loadModel` is `() => import(...).then((m) => m.buildFoo)`, so a renamed
    // or missing export RESOLVES with undefined rather than rejecting. Caught
    // here it reports itself; uncaught it is indistinguishable from a chunk
    // that never arrived.
    if (typeof fn !== 'function') {
      throw new Error(`loadModel resolved to ${fn === undefined ? 'undefined' : typeof fn}, expected the build function — is the export named correctly?`);
    }
    return fn;
  } catch (err) {
    clearTimeout(timer);
    // One retry: a chunk request dropped in flight succeeds on a second ask,
    // whereas a genuinely missing chunk fails the same way twice and costs
    // only one extra round trip to establish that.
    if (attempt === 0) {
      console.warn(`[weapons] "${id}" model failed, retrying —`, err?.message ?? err);
      return loadWeaponModel(id, failures, 1);
    }
    const reason = String(err?.message ?? err);
    console.warn(`[weapons] "${id}" model did not load — skipping it:`, reason);
    failures?.push(`${id}: ${reason}`);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * WEAPONS — weapon meshes, the first-person viewmodel rig, ADS, recoil, sway,
 * bob, reload/inspect animation and projectile ballistics.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHAT LIVES HERE
 *   geometry.js   hard-surface kit: chamfered boxes, lathes, extrusions,
 *                 Picatinny rail, M-LOK, knurling, screws, and the Assembly
 *                 that merges everything down to a handful of draw calls.
 *   parts.js      real firearm components built from published dimensions:
 *                 receivers, barrels, muzzle devices, handguards, stocks,
 *                 grips, magazines, optics, iron sights, triggers.
 *   models/*.js   the three weapons assembled from those parts.
 *   hands.js      gloved hands + sleeved arms, two-bone IK from the hand.
 *   viewmodel.js  the animation stack (sway/bob/lag/recoil/ADS/clips).
 *   clips.js      keyframed reload / inspect / draw timelines.
 *   ballistics.js travelling projectiles with gravity and drag.
 *   defs.js       every tuning number, plus the deterministic recoil patterns.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * PUBLIC API — `const wp = ctx.get('weapons')`
 * ────────────────────────────────────────────────────────────────────────────
 *   wp.current            { id, label, class, mode, magSize, ... } (the def)
 *   wp.ammo               { mag, chambered, reserve, magSize, total, empty }
 *   wp.fireMode           'auto' | 'burst' | 'semi'
 *   wp.spreadDegrees      live cone half-angle — drive the crosshair gap with it
 *   wp.adsProgress        0..1
 *   wp.reloading / wp.firing / wp.switching / wp.inspecting
 *   wp.weaponIds          unlocked weapon ids, in slot order
 *   wp.grenades           { frag, flash } counts left
 *   wp.throwFrag()        lob a frag grenade along the camera's aim
 *   wp.throwFlash()       lob a flashbang along the camera's aim
 *   wp.setWeapon(id)      draw/holster animated swap
 *   wp.nextWeapon()
 *   wp.cycleFireMode()
 *   wp.reload()           no-op if full or empty of reserve
 *   wp.inspect()
 *   wp.tryFire()          honours fire mode + rpm; returns true if a shot left
 *   wp.viewmodel          the rig (fx/ui may read muzzle/eject transforms)
 *   wp.muzzleWorld(v3)    world-space muzzle, for anything that needs it
 *   wp.debugPose(kind)    'idle' | 'ads' | 'fire'  (the capture harness)
 *   wp.stats              { tris, drawCalls, live, fired }
 *
 * EVENTS EMITTED  (all canonical, see ARCHITECTURE.md)
 *   weapon:fire    { weapon, origin, dir, seed }
 *   weapon:shell   { position, velocity }
 *   weapon:reload  { weapon, phase: 'start'|'magout'|'magin'|'end' }
 *   bullet:tracer  { from, to, speed }
 * `bullet:impact` comes from physics, because physics owns penetration.
 * Anything else (ammo counts, fire mode, the current weapon) is a getter on
 * this object rather than an event, so no new event types are introduced.
 */
export class WeaponSystem {
  static id = 'weapons';
  static deps = ['materials', 'physics'];

  constructor() {
    this.viewmodel = null;
    this.sim = null;
    this.states = new Map();
    this.activeId = 'rifle';
    // Replaced in init() with whatever the loadout actually built — this is
    // just what `weaponIds` reports if read before init() resolves.
    this.unlockedWeaponIds = new Set(Object.values(DEFAULT_LOADOUT).filter((id) => WEAPON_DEFS[id]));
    this.debugMode = null;

    this._fireTimer = 0;
    this._burstLeft = 0;
    this._burstCooldown = 0;
    this._semiLatch = false;
    this._spread = 0;
    this._shotIndex = 0;
    this._sinceShot = 10;
    this._switchTimer = 0;
    this._switchTo = null;
    this._reloadPhase = null;

    this._muzzle = new THREE.Vector3();
    this._dir = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._up = new THREE.Vector3();
    this._tmp = new THREE.Vector3();
    this._camDir = new THREE.Vector3();
    this._firePayload = { weapon: null, origin: new THREE.Vector3(), dir: new THREE.Vector3(), seed: 0 };
    this._reloadPayload = { weapon: null, phase: 'start' };
    // `weapon:shell` carries the canonical { position, velocity } plus the real
    // case dimensions and a spin, so fx can size and tumble the brass instead of
    // guessing: a 9x19 case is less than half the length of a 5.56x45 one.
    this._shellPayload = {
      position: new THREE.Vector3(),
      velocity: new THREE.Vector3(),
      weapon: null,
      caseLen: 0.0446,
      caseRadius: 0.00495,
      spin: 0,
    };
    this._pendingShots = 0;
    this._pendingFirst = false;

    // Deferred shell ejections (a case leaves the port a few ms after the shot).
    this._shellQueue = [];
    for (let i = 0; i < 8; i++) {
      this._shellQueue.push({ t: -1, pos: new THREE.Vector3(), vel: new THREE.Vector3() });
    }
    this._droppedMags = [];
    this._pelletDir = new THREE.Vector3();

    // ---- gun-bash melee (V key, any equipped weapon) ----
    this._bashTimer = 0;

    // ---- throwables (frag / flash) ----
    this.grenades = {
      frag: THROWABLE_DEFS.frag.startCount,
      flash: THROWABLE_DEFS.flash.startCount,
    };
    this._liveThrowables = [];
    this._throwTmp = new THREE.Vector3();
    this._state = {
      ads: false,
      sprint: false,
      lowReady: false,
      speed: 0,
      crouch: false,
      airborne: false,
      trigger: false,
      empty: false,
    };
    // Preallocated HUD snapshot handed to `ui` (see getHudState).
    this._hudState = {
      name: '', mode: 'auto', ammo: 0, reserve: 0, magSize: 0,
      reloading: false, reloadProgress: 0, ads: false, spread: 0, firing: false,
    };
  }

  /* ====================================================================== */
  /*  init                                                                  */
  /* ====================================================================== */

  async init(ctx) {
    this.ctx = ctx;
    this.rng = ctx.rng.fork();
    this.mats = new WeaponMaterials(ctx);
    this.sim = new ProjectileSim(ctx);
    this.viewmodel = new Viewmodel(ctx, this.mats);
    // three only honours `material.envMapIntensity` when the material carries its
    // OWN `envMap`; for a material lit by `scene.environment` the renderer
    // overwrites that uniform with `scene.environmentIntensity` every frame
    // (WebGLRenderer.setProgram, the isMeshStandardMaterial branch). The
    // viewmodel is drawn from its own scene, so ENV_OCCLUSION — how much of the
    // sky a shouldered weapon actually sees, see materials.js — has to be
    // expressed there or it is silently a no-op.
    ctx.viewScene.environmentIntensity = ENV_OCCLUSION;
    this.viewmodel.onClipEvent = (name, clip) => this._onClipEvent(name, clip);

    // Owning any number of weapons must cost nothing at runtime: only the
    // player's four equipped slots are ever built into the viewmodel. At 50 weapons, building
    // all of them here was a multi-second boot stall and fifty weapons' worth
    // of permanent viewScene materials. `ctx.config.loadout` is the hook the
    // armory/store (econ.js) writes before a match; absent that, a fresh
    // player gets the rifle/pistol default.
    const rawLoadout = ctx.config?.loadout ?? DEFAULT_LOADOUT;
    const loadout = Array.isArray(rawLoadout)
      ? { ...DEFAULT_LOADOUT, primary: rawLoadout[0], secondary: rawLoadout[1] }
      : { ...DEFAULT_LOADOUT, ...rawLoadout };
    this.loadout = loadout;
    this.slotWeaponIds = LOADOUT_SLOTS.map((slot) => loadout[slot]).filter((id) => WEAPON_DEFS[id]);
    const equipped = [...new Set(this.slotWeaponIds)];
    this.unlockedWeaponIds = new Set(equipped);
    // The starter grenade occupies utility; equipping the RPG replaces it.
    this.grenades.frag = loadout.utility === 'frag' ? THROWABLE_DEFS.frag.startCount : 0;

    const t0 = performance.now();
    let tris = 0;
    const failures = [];
    const buildFns = await Promise.all(equipped.map((id) => loadWeaponModel(id, failures)));
    // Losing ONE gun is survivable. Losing them all is not: the viewmodel still
    // draws the arms, so the player lands in the world holding two bare
    // forearms and no explanation. Fail loudly, and carry the ACTUAL reasons —
    // a guessed cause in this message sent a whole debugging round the wrong way.
    if (buildFns.every((fn) => !fn)) {
      throw new Error(`No weapon model loaded. ${failures.join(' | ')}`);
    }
    for (let i = 0; i < equipped.length; i++) {
      const id = equipped[i];
      // A chunk that never arrived costs the player one gun. Waiting on it, or
      // throwing, costs them the whole session — this is the only await in the
      // entire boot chain, so nothing else can fail this way.
      if (!buildFns[i]) continue;
      const def = { ...WEAPON_DEFS[id] };
      def.cycleTime = 60 / def.rpm;
      const model = buildFns[i]();
      const entry = this.viewmodel.addWeapon(model, def);
      tris += entry.tris;
      this.states.set(id, {
        def,
        pattern: buildRecoilPattern(def, Rng),
        mag: def.magSize,
        chambered: true,
        reserve: def.reserve,
        mode: def.modes[0],
        modeIndex: 0,
      });
    }
    // Fall back to a weapon that actually BUILT, not merely one that was
    // equipped: a skipped chunk would otherwise leave activeId naming a weapon
    // the viewmodel has never heard of.
    if (!this.states.has(this.activeId)) this.activeId = [...this.states.keys()][0];
    // Every equipped weapon is built and fitted, so the hands can stop being
    // 86 draw calls a frame. Must come after the loop — see
    // Viewmodel.finalizeHands().
    this.viewmodel.finalizeHands();
    this.viewmodel.setActive(this.activeId);
    this.viewmodel.play('draw');

    // Player hooks (all optional: the viewmodel works standalone).
    this.player = ctx.peek('player');
    this.fx = ctx.peek('fx');
    this.physics = ctx.peek('physics');
    this._off = [];
    this._off.push(
      ctx.events.on('player:land', (e) => this.viewmodel.land(Math.abs(e?.velocity ?? 3)))
    );
    this._off.push(ctx.events.on('player:jump', () => this.viewmodel.jump()));

    this.stats = { tris, drawCalls: 0, live: 0, fired: 0 };
    console.info(
      `[weapons] ${this.states.size} weapons · ${(tris / 1000).toFixed(1)}k tris viewmodel · ` +
        `built in ${(performance.now() - t0).toFixed(0)}ms`
    );
  }

  /* ====================================================================== */
  /*  public getters                                                        */
  /* ====================================================================== */

  get state() {
    return this.states.get(this.activeId);
  }

  get current() {
    return this.state?.def ?? null;
  }

  get weaponIds() {
    return [...this.states.keys()].filter((id) => this.unlockedWeaponIds.has(id));
  }

  get ammo() {
    const s = this.state;
    if (!s) return { mag: 0, chambered: false, reserve: 0, magSize: 0, total: 0, empty: true };
    const mag = s.mag;
    const ch = s.chambered ? 1 : 0;
    return {
      mag: mag + ch,
      inMag: mag,
      chambered: s.chambered,
      reserve: s.reserve,
      magSize: s.def.magSize,
      total: mag + ch + s.reserve,
      empty: mag + ch === 0,
    };
  }

  get fireMode() {
    return this.state?.mode ?? 'semi';
  }

  get adsProgress() {
    return this.viewmodel?.adsT ?? 0;
  }

  get reloading() {
    const n = this.viewmodel?.clipName;
    return n === 'reloadTac' || n === 'reloadEmpty';
  }

  get inspecting() {
    return this.viewmodel?.clipName === 'inspect';
  }

  get switching() {
    return this._switchTo !== null;
  }

  get firing() {
    return this._sinceShot < 0.12;
  }

  /** Current spread cone half-angle in degrees — the crosshair should use this. */
  get spreadDegrees() {
    return this._spread;
  }

  muzzleWorld(out) {
    return this.viewmodel.muzzleWorld(out ?? this._tmp);
  }

  /**
   * HUD adapter polled by `ui` every lateUpdate. Shape is fixed by the contract
   * documented at the top of src/ui/index.js; the object is preallocated and
   * mutated in place because `ui` reads it once per frame and never keeps it.
   */
  getHudState() {
    const h = this._hudState;
    const s = this.state;
    if (!s) return h;
    const a = this.ammo;
    const vm = this.viewmodel;
    h.name = s.def.label ?? s.def.id;
    h.mode = s.mode;
    // `a.mag` counts the chambered round, so a topped-off rifle is 31. The HUD
    // draws one pip per round against magSize, so clamp the *display* to the
    // magazine capacity rather than overflowing the pip strip.
    h.ammo = Math.min(a.mag, a.magSize);
    h.reserve = a.reserve;
    h.magSize = a.magSize;
    h.reloading = this.reloading;
    // 0..1 through the active reload clip; the bar is meaningless otherwise.
    h.reloadProgress = h.reloading && vm?.clip?.duration
      ? Math.min(1, vm.clipT / vm.clip.duration)
      : 0;
    h.ads = (vm?.adsT ?? 0) > 0.5;
    // `ui` maps this to reticle bloom as 4 + spread * 40 px, so hand it a
    // normalised 0..1 rather than raw degrees.
    h.spread = Math.min(1, Math.max(0, this._spread / 6));
    h.firing = this.firing;
    h.lethalCount = this.grenades.frag;
    h.tacticalCount = this.grenades.flash;
    return h;
  }

  /* ====================================================================== */
  /*  weapon management                                                     */
  /* ====================================================================== */

  setWeapon(id) {
    if (!this.unlockedWeaponIds.has(id) || !this.states.has(id) || id === this.activeId || this._switchTo) return false;
    this._switchTo = id;
    this._switchTimer = this.viewmodel.play('holster');
    return true;
  }

  nextWeapon() {
    const ids = this.weaponIds;
    const i = ids.indexOf(this.activeId);
    return this.setWeapon(ids[(i + 1) % ids.length]);
  }

  cycleFireMode() {
    const s = this.state;
    if (!s || s.def.modes.length < 2) return s?.mode;
    s.modeIndex = (s.modeIndex + 1) % s.def.modes.length;
    s.mode = s.def.modes[s.modeIndex];
    this._burstLeft = 0;
    return s.mode;
  }

  reload() {
    const s = this.state;
    if (!s || this.reloading || this.switching) return false;
    if (s.mag >= s.def.magSize || s.reserve <= 0) return false;
    this.viewmodel.stopClip();
    const empty = s.mag === 0 && !s.chambered;
    this.viewmodel.play(empty ? 'reloadEmpty' : 'reloadTac');
    this._pendingReloadEmpty = empty;
    return true;
  }

  /** Refill every equipped slot after a life is spent. */
  resetForRespawn() {
    for (const s of this.states.values()) {
      s.mag = s.def.magSize;
      s.chambered = s.def.magSize > 0;
      s.reserve = s.def.reserve;
    }
    this.grenades.frag = this.loadout?.utility === 'frag' ? THROWABLE_DEFS.frag.startCount : 0;
    this.grenades.flash = THROWABLE_DEFS.flash.startCount;
    this._fireTimer = 0;
    this._spread = 0;
    this._shotIndex = 0;
  }

  inspect() {
    if (this.reloading || this.switching || this.inspecting) return false;
    this.viewmodel.play('inspect');
    return true;
  }

  /* ====================================================================== */
  /*  firing                                                                */
  /* ====================================================================== */

  canFire() {
    const s = this.state;
    if (!s) return false;
    if (this.reloading || this.switching) return false;
    if (this._fireTimer > 0) return false;
    return s.chambered;
  }

  /** One round leaves the barrel. Returns false if the trigger clicked dry. */
  tryFire() {
    const s = this.state;
    if (!s) return false;
    if (this.reloading || this.switching || this._fireTimer > 0) return false;
    if (s.def.class === 'melee') return this._meleeAttack(s, s.def);
    if (!s.chambered) {
      // Dry: lock the bolt back and let the player know by feel.
      this.viewmodel.boltHold = 1;
      this._fireTimer = 0.25;
      return false;
    }
    if (this.inspecting) this.viewmodel.stopClip();

    const def = s.def;
    const first = this._sinceShot > 0.35;
    // ---- feed the next round ----
    s.chambered = false;
    if (s.mag > 0) {
      s.mag--;
      s.chambered = true;
    } else {
      this.viewmodel.boltHold = 1;
    }

    // ---- deterministic recoil pattern ----
    const idx = Math.min(this._shotIndex, def.recoil.patternLength - 1);
    const pitch = s.pattern[idx * 2];
    const yaw = s.pattern[idx * 2 + 1];
    this._shotIndex++;

    // ---- aim: camera forward + a spread cone ----
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    this._camDir.set(0, 0, -1).applyQuaternion(cam.quaternion).normalize();
    this._dir.copy(this._camDir);
    const spreadRad = this._spread * DEG;
    const pellets = def.pellets ?? 1;
    if (spreadRad > 1e-5 || pellets > 1) {
      const d = this.rng.disc(this._disc ?? (this._disc = { x: 0, y: 0 }));
      this._right.set(1, 0, 0).applyQuaternion(cam.quaternion);
      this._up.set(0, 1, 0).applyQuaternion(cam.quaternion);
      this._dir
        .addScaledVector(this._right, Math.tan(spreadRad) * d.x)
        .addScaledVector(this._up, Math.tan(spreadRad) * d.y)
        .normalize();
    }

    // ---- projectile(s) ----
    // A shotgun fires `def.pellets` rounds per trigger pull, each with its own
    // scatter on top of the shared spread cone above; everything else about
    // the shot (recoil, shell eject, fire timer) still happens once.
    this.viewmodel.muzzleWorld(this._muzzle);
    const seed = this.rng.u32();
    for (let pi = 0; pi < pellets; pi++) {
      let dir = this._dir;
      if (pellets > 1) {
        const pelletRad = (def.pelletSpread ?? 0) * DEG;
        const pd = this.rng.disc(this._pelletDisc ?? (this._pelletDisc = { x: 0, y: 0 }));
        this._pelletDir
          .copy(this._dir)
          .addScaledVector(this._right, Math.tan(pelletRad) * pd.x)
          .addScaledVector(this._up, Math.tan(pelletRad) * pd.y)
          .normalize();
        dir = this._pelletDir;
      }
      this.sim.spawn({
        origin: this._muzzle,
        dir,
        speed: def.muzzleVelocity,
        damage: def.damage,
        penetration: def.penetration,
        dragK: def.dragK,
        dropoff: def.dropoff,
        maxRange: def.maxRange,
        weapon: def,
        tracer: pi === 0 && this.stats.fired % def.tracerEvery === 0,
      });
    }

    // ---- feedback ----
    this.viewmodel.addRecoil(pitch, yaw, first);
    const p = this.player;
    if (p?.addRecoil) {
      // The camera climb is the learnable part; the viewmodel kick is the feel.
      p.addRecoil(pitch, yaw, def.recoil.roll * 0.35, def.recoil.punch);
    }
    this._spread = Math.min(def.spreadMax, this._spread + def.spreadPerShot);
    this._fireTimer = 60 / def.rpm;
    this._sinceShot = 0;
    this.stats.fired++;
    this._pendingShots++;
    this._pendingFirst = this._pendingFirst || first;
    this._fireSeed = seed;

    // Shell leaves the port shortly after the shot, once the bolt is back.
    if (def.ejectsShell !== false) this._queueShell(Math.min(0.05, this._fireTimer * 0.45));
    return true;
  }

  _queueShell(delay) {
    for (const q of this._shellQueue) {
      if (q.t < 0) {
        q.t = delay;
        return q;
      }
    }
    return null;
  }

  /* ====================================================================== */
  /*  melee                                                                 */
  /* ====================================================================== */

  /** Knife/fists trigger pull: a short raycast instead of a projectile. */
  _meleeAttack(s, def) {
    this._fireTimer = 60 / def.rpm;
    this._sinceShot = 0;
    this._swing(def.damage, def.meleeRange ?? 1.3, def.recoil, def);
    this.stats.fired++;
    return true;
  }

  /**
   * Gun-bash: the `melee` action (V) always works, whatever is equipped, on
   * its own cooldown separate from the trigger. Knives/fists ignore it —
   * their own trigger pull is already a proper attack.
   */
  _bash() {
    const s = this.state;
    if (!s || s.def.class === 'melee' || this._bashTimer > 0 || this.reloading || this.switching) {
      return false;
    }
    this._bashTimer = 0.75;
    this._swing(40, 1.2, s.def.recoil);
    return true;
  }

  /** Shared melee raycast: reuses the bullet terminal-effects path so hits
   * get the same damage/impact/blood-decal handling a bullet gets. */
  _swing(damage, range, recoil, def = null) {
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    this._camDir.set(0, 0, -1).applyQuaternion(cam.quaternion).normalize();
    const phys = this.physics ?? (this.physics = this.ctx.peek('physics'));
    if (def?.backstabDamage && phys?.raycast) {
      const hit = phys.raycast(cam.position, this._camDir, range, phys.MASK?.BULLET);
      const actor = hit?.actor;
      if (actor && Number.isFinite(actor.yaw)) {
        // AI forward is (+sin(yaw), 0, +cos(yaw)). A strike travelling in the
        // same direction entered through the victim's back.
        const dot = this._camDir.x * Math.sin(actor.yaw) + this._camDir.z * Math.cos(actor.yaw);
        if (dot >= (def.backstabDot ?? 0.55)) damage = def.backstabDamage;
      }
    }
    phys?.fireBullet?.({
      origin: cam.position,
      dir: this._camDir,
      damage,
      penetration: 0,
      maxDist: range,
      rng: this.rng,
    });
    this.viewmodel.addRecoil(recoil.pitch, recoil.yaw, true);
  }

  /* ====================================================================== */
  /*  throwables — frag grenade / flashbang                                 */
  /* ====================================================================== */

  throwFrag() {
    return this._throw('frag');
  }

  throwFlash() {
    return this._throw('flash');
  }

  _throw(kind) {
    if (this.grenades[kind] <= 0 || this.switching) return false;
    const phys = this.physics ?? (this.physics = this.ctx.peek('physics'));
    if (!phys?.addRigidBody) return false;
    this.grenades[kind]--;

    const tdef = THROWABLE_DEFS[kind];
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    this._camDir.set(0, 0, -1).applyQuaternion(cam.quaternion).normalize();
    const from = this._throwTmp.copy(cam.position).addScaledVector(this._camDir, 0.35);

    const geo = this._ensureThrowableGeo(kind);
    const mesh = new THREE.Mesh(geo.geometry, geo.material);
    this.ctx.scene.add(mesh);

    const vel = {
      x: this._camDir.x * tdef.throwSpeed,
      y: this._camDir.y * tdef.throwSpeed + 1.2,
      z: this._camDir.z * tdef.throwSpeed,
    };
    const pv = this.player?.velocity;
    if (pv) {
      vel.x += pv.x;
      vel.y += pv.y * 0.3;
      vel.z += pv.z;
    }
    const body = phys.addRigidBody({
      shape: 'sphere',
      radius: 0.045,
      mass: 0.4,
      position: from,
      velocity: vel,
      restitution: 0.32,
      friction: 0.7,
      lifetime: tdef.fuse + 6,
      object3D: mesh,
      surfaceType: 'metal',
    });
    this._liveThrowables.push({ kind, body, mesh, fuse: tdef.fuse });
    this.ctx.peek('ui')?.spawnGrenade?.(from.clone(), tdef.fuse);
    return true;
  }

  /** Grenade/flashbang geometry, built once and shared across every throw. */
  _ensureThrowableGeo(kind) {
    if (!this._throwableGeo) this._throwableGeo = {};
    let g = this._throwableGeo[kind];
    if (g) return g;
    if (kind === 'flash') {
      g = {
        geometry: new THREE.CylinderGeometry(0.032, 0.032, 0.11, 14),
        material: new THREE.MeshStandardMaterial({ color: 0xb8bdb0, roughness: 0.45, metalness: 0.75 }),
      };
    } else {
      g = {
        geometry: new THREE.IcosahedronGeometry(0.045, 1),
        material: new THREE.MeshStandardMaterial({ color: 0x2c3226, roughness: 0.62, metalness: 0.85 }),
      };
    }
    this._throwableGeo[kind] = g;
    return g;
  }

  _updateThrowables(dt) {
    if (!this._liveThrowables.length) return;
    for (let i = this._liveThrowables.length - 1; i >= 0; i--) {
      const t = this._liveThrowables[i];
      t.fuse -= dt;
      if (t.fuse > 0) continue;
      const p = t.body?.position ?? t.mesh.position;
      const tdef = THROWABLE_DEFS[t.kind];
      if (t.kind === 'flash') {
        this.ctx.events.emit('flashbang', {
          position: new THREE.Vector3(p.x, p.y, p.z),
          radius: tdef.radius,
        });
      } else {
        this.ctx.events.emit('explosion', {
          position: new THREE.Vector3(p.x, p.y, p.z),
          radius: tdef.radius,
          damage: tdef.damage,
          source: 'player',
        });
      }
      this.physics?.removeRigidBody(t.body);
      this.ctx.scene.remove(t.mesh);
      this._liveThrowables.splice(i, 1);
    }
  }

  /* ====================================================================== */
  /*  reload / clip callbacks                                               */
  /* ====================================================================== */

  _onClipEvent(name, clipName) {
    const s = this.state;
    const isReload = clipName === 'reloadTac' || clipName === 'reloadEmpty';
    switch (name) {
      case 'start':
        if (isReload) this._emitReload('start');
        break;
      case 'magout':
        if (isReload) this._emitReload('magout');
        break;
      case 'magdrop':
        if (isReload) this._dropMagazine();
        break;
      case 'magin':
        if (isReload) {
          this._emitReload('magin');
          this._completeReload(clipName === 'reloadEmpty');
        }
        break;
      case 'boltrelease':
        this.viewmodel.boltHold = 0;
        break;
      case 'end':
        if (isReload) {
          this._emitReload('end');
          this.viewmodel.boltHold = 0;
        }
        if (clipName === 'holster' && this._switchTo) {
          this.activeId = this._switchTo;
          this._switchTo = null;
          this.viewmodel.setActive(this.activeId);
          this.viewmodel.play('draw');
          this._shotIndex = 0;
          this._spread = 0;
        }
        break;
      default:
        break;
    }
  }

  /**
   * The chambered-round model: a tactical reload keeps the round in the chamber
   * and gives you magSize+1; an empty reload has to feed one out of the fresh
   * magazine, so you end up with exactly magSize.
   */
  _completeReload(empty) {
    const s = this.state;
    if (!s) return;
    const want = s.def.magSize - s.mag;
    const take = Math.min(want, s.reserve);
    s.reserve -= take;
    s.mag += take;
    if (empty && !s.chambered && s.mag > 0) {
      s.mag--;
      s.chambered = true;
    }
    this._shotIndex = 0;
  }

  _emitReload(phase) {
    this._reloadPayload.weapon = this.current;
    this._reloadPayload.phase = phase;
    this.ctx.events.emit('weapon:reload', this._reloadPayload);
  }

  /** Spawn the discarded magazine as a real rigid body in the world. */
  _dropMagazine() {
    const phys = this.physics ?? (this.physics = this.ctx.peek('physics'));
    const w = this.viewmodel.active;
    // Tube-fed (shotgun) and melee weapons have no `magazine` part, but their
    // reload clip still fires the generic `magdrop` event — nothing to drop.
    if (!w || !w.parts.magazine) return;
    const proxy = this._magProxy(w);
    if (!proxy) return;
    const mag = w.parts.magazine;
    mag.updateMatrixWorld();
    proxy.group.position.setFromMatrixPosition(mag.matrixWorld);
    proxy.group.quaternion.setFromRotationMatrix(mag.matrixWorld);
    proxy.group.visible = true;
    // Magazine geometry hangs below its origin, so bias the body centre down.
    const half = w.magLen * 0.45;
    proxy.group.position.y -= half * 0.4;

    const vel = this._tmp.set(0, -0.7, 0);
    const pv = this.player?.velocity;
    if (pv) vel.add(pv);
    vel.x += this.rng.signed() * 0.25;
    vel.z += this.rng.signed() * 0.25;

    if (phys?.spawnDebris) {
      proxy.body = phys.spawnDebris(proxy.group.position, vel, {
        size: Math.max(0.02, w.magLen * 0.28),
        surface: 'rubber',
        mass: 0.38,
        lifetime: 22,
        restitution: 0.18,
        object3D: proxy.group,
      });
      proxy.until = this.ctx.time.elapsed + 22;
    } else {
      proxy.until = this.ctx.time.elapsed + 2;
    }
  }

  /** Two reusable world-space magazine props per weapon. */
  _magProxy(w) {
    if (!this._magPools) this._magPools = new Map();
    let pool = this._magPools.get(w.id);
    if (!pool) {
      pool = [];
      for (let i = 0; i < 2; i++) {
        const group = new THREE.Object3D();
        group.name = `dropped-mag-${w.id}-${i}`;
        group.visible = false;
        // Share the viewmodel's geometry and materials; the world copy needs no
        // resources of its own.
        w.parts.magazine.traverse((o) => {
          if (o.isMesh) {
            const m = new THREE.Mesh(o.geometry, o.material);
            m.position.copy(o.position);
            m.quaternion.copy(o.quaternion);
            m.castShadow = true;
            group.add(m);
          }
        });
        this.ctx.scene.add(group);
        pool.push({ group, body: null, until: 0 });
        this._droppedMags.push(pool[i]);
      }
      this._magPools.set(w.id, pool);
    }
    // Reuse the oldest.
    let best = pool[0];
    for (const p of pool) if (p.until < best.until) best = p;
    if (best.body && this.physics?.removeRigidBody) this.physics.removeRigidBody(best.body);
    best.body = null;
    return best;
  }

  /* ====================================================================== */
  /*  frame                                                                 */
  /* ====================================================================== */

  fixedUpdate(h) {
    this.sim.fixedUpdate(h);
  }

  update(dt, ctx) {
    const s = this.state;
    if (!s) return;
    const def = s.def;
    const input = ctx.input;
    const player = this.player ?? (this.player = ctx.peek('player'));
    const st = this._state;

    this._sinceShot += dt;
    if (this._fireTimer > 0) this._fireTimer -= dt;
    if (this._burstCooldown > 0) this._burstCooldown -= dt;
    if (this._bashTimer > 0) this._bashTimer -= dt;
    this._updateThrowables(dt);

    // ---- spread recovery -------------------------------------------------
    const rest = this._restSpread(def, player, st);
    this._spread = Math.max(rest, this._spread - def.spreadDecay * dt * (1 + this.adsProgress));
    if (this._sinceShot > 0.6) this._shotIndex = 0;

    // ---- gather state ----------------------------------------------------
    const live =
      !input.frozen && input.enabled !== false && this.debugMode === null &&
      player?.controlEnabled !== false && player?.dead !== true;
    st.ads = live ? input.ads || player?.adsRequested === true : this.debugMode === 'ads';
    st.sprint = live ? player?.sprinting === true && this._sinceShot > 0.3 : false;
    st.speed = player?.horizontalSpeed ?? player?.speed ?? 0;
    st.crouch = player?.stance === 'crouch';
    st.airborne = player?.airborne === true;
    st.lowReady = player?.state === 'mantle' || player?.mantling === true;
    st.empty = s.mag === 0 && !s.chambered;

    // ---- input -----------------------------------------------------------
    if (live) {
      if (input.actionPressed('reload')) this.reload();
      if (input.pressed('KeyB')) this.cycleFireMode();
      if (input.pressed('KeyI')) this.inspect();
      // The number row maps directly to Primary, Secondary, Melee, Utility.
      for (let slot = 0; slot < LOADOUT_SLOTS.length; slot++) {
        if (input.pressed(`Digit${slot + 1}`)) this.setWeapon(this.slotWeaponIds[slot]);
      }
      if (input.pressed('Tab')) this.nextWeapon();
      if (input.wheel) this.nextWeapon();
      if (input.actionPressed('melee')) this._bash();
      if (input.actionPressed('grenade')) this.throwFrag();
      if (input.actionPressed('flashbang')) this.throwFlash();
      this._runTrigger(dt, input.fire, input.firePressed, def, s);
      st.trigger = input.fire && this.canFire();
      // Auto-reload on a dry trigger pull, like every modern shooter. Melee
      // weapons always read `empty` (they carry no ammo) but `reload()`
      // already no-ops for them (`s.mag(0) >= s.def.magSize(0)`).
      if (input.firePressed && st.empty) this.reload();
    } else if (this.debugMode) {
      this._runDebug(ctx);
      st.trigger = this._sinceShot < 0.09;
    }

    // Push the ADS curve to the player so camera FOV / move speed follow it.
    player?.setAdsProgress?.(this.viewmodel.adsT, def.adsFov);

    this.stats.live = this.sim.stats.live;
    this.stats.fired = this.sim.stats.fired;
  }

  /** Fire-mode state machine. */
  _runTrigger(dt, held, pressed, def, s) {
    switch (s.mode) {
      case 'auto':
        if (held) this.tryFire();
        break;
      case 'burst':
        if (pressed && this._burstLeft === 0 && this._burstCooldown <= 0) {
          this._burstLeft = def.burstCount;
        }
        if (this._burstLeft > 0 && this._fireTimer <= 0) {
          if (this.tryFire()) {
            this._burstLeft--;
            this._fireTimer = 60 / def.burstRpm;
            if (this._burstLeft === 0) this._burstCooldown = def.burstDelay;
          } else {
            this._burstLeft = 0;
          }
        }
        break;
      default: // semi
        if (pressed) this.tryFire();
        break;
    }
  }

  _restSpread(def, player, st) {
    let base = lerp(def.spreadHip, def.spreadAds, this.adsProgress);
    if (st.crouch) base *= SPREAD_MODS.crouch;
    if (player?.stance === 'prone') base *= SPREAD_MODS.prone;
    if (st.speed < 0.4) base *= SPREAD_MODS.still;
    else if (st.speed > 3.2) base *= SPREAD_MODS.walking;
    if (st.sprint) base *= SPREAD_MODS.sprinting;
    if (st.airborne) base *= SPREAD_MODS.airborne;
    return base;
  }

  lateUpdate(dt, ctx) {
    const vm = this.viewmodel;
    if (!vm) return;
    vm.update(dt, this._state);

    // ---- muzzle flash / audio, now that the pose is final ---------------
    if (this._pendingShots > 0) {
      const def = this.current;
      vm.muzzleWorld(this._firePayload.origin);
      vm.boreDir(this._firePayload.dir);
      this._firePayload.weapon = def;
      this._firePayload.seed = this._fireSeed >>> 0;
      for (let i = 0; i < this._pendingShots; i++) {
        ctx.events.emit('weapon:fire', this._firePayload);
      }
      this._pendingShots = 0;
      this._pendingFirst = false;
    }

    // ---- deferred shell ejection ---------------------------------------
    for (const q of this._shellQueue) {
      if (q.t < 0) continue;
      q.t -= dt;
      if (q.t > 0) continue;
      q.t = -1;
      vm.ejectWorld(this._shellPayload.position);
      vm.ejectVelocity(this._shellPayload.velocity, 2.3 + this.rng.float() * 1.2);
      const pv = this.player?.velocity;
      if (pv) this._shellPayload.velocity.add(pv);
      this._shellPayload.velocity.y += 1.1;
      this._shellPayload.weapon = this.current;
      const shell = vm.active?.shell;
      this._shellPayload.caseLen = shell?.caseLen ?? 0.0446;
      this._shellPayload.caseRadius = shell?.rimR ?? 0.00495;
      this._shellPayload.spin = 28 + this.rng.float() * 34;
      ctx.events.emit('weapon:shell', this._shellPayload);
    }

    // ---- retire dropped magazines --------------------------------------
    if (this._droppedMags.length) {
      const now = ctx.time.elapsed;
      for (const p of this._droppedMags) {
        if (p.group.visible && p.until && now > p.until) {
          p.group.visible = false;
          if (p.body && this.physics?.removeRigidBody) {
            this.physics.removeRigidBody(p.body);
            p.body = null;
          }
        }
      }
    }
  }

  /* ====================================================================== */
  /*  capture harness                                                       */
  /* ====================================================================== */

  /**
   * Freeze the viewmodel in a photogenic state.
   * The harness applies a shot, then pumps `SETTLE` frames before grabbing the
   * frame, so 'fire' schedules a short burst that peaks right at the capture.
   */
  debugPose(kind = 'idle', opts = {}) {
    const vm = this.viewmodel;
    this.debugMode = kind;
    this.setWeaponImmediate('rifle');
    vm.stopClip();
    vm.recPos.reset();
    vm.recRot.reset();
    vm.settle.reset();
    vm.lag.reset();
    vm.lagRot.reset();
    vm.boltHold = 0;
    vm.boltCycle = 0;
    vm.sprintT = 0;
    vm.lowReadyT = 0;
    vm.bobPhase = 0;
    vm._angVel.yaw = 0;
    vm._angVel.pitch = 0;
    vm._hasPrev = false;
    // A fixed, non-zero noise phase: a settled but not artificially symmetric pose.
    vm.noiseT = 12.37;
    vm.debugFrozen = true;
    this._spread = kind === 'ads' ? 0.24 : 2.05;
    this._sinceShot = 10;
    this._debugFrame = 0;

    const s = this.state;
    if (s) {
      s.mag = kind === 'fire' ? 22 : s.def.magSize;
      s.chambered = true;
      s.reserve = s.def.reserve;
    }

    if (kind === 'ads') {
      vm.adsT = 1;
      this._state.ads = true;
    } else {
      vm.adsT = 0;
      this._state.ads = false;
    }
    this._state.sprint = false;
    this._state.speed = 0;
    this._state.trigger = false;
    // Frames (at the harness's fixed 60 Hz) on which to fire for the 'fire'
    // shot. The burst has to land at the END of the harness's settle window: a
    // flash core lives 52 ms (~3 frames), so the last rounds must leave the
    // barrel a frame or two before the grab or there is nothing to photograph.
    // `grabFrame` is how many frames the harness will pump — it is a CLI flag
    // (`--settle`), so it cannot be hard-coded here. The offsets below straddle
    // the grab because the harness pumps on its own rAF chain, which can land a
    // frame either side of the engine's.
    // A flash core lives 52 ms — about three frames at 60 Hz — while the exact
    // frame the shutter lands on is only known to within a handful of frames
    // (the harness pumps its settle count on its own rAF chain, then the
    // screenshot RPC costs a few more). So: three spaced rounds early to fill
    // the frame with drifting smoke, brass in flight and a tracer, then a
    // sustained tail on a 2-frame cadence, so a flash is lit continuously
    // across the whole uncertainty window.
    //
    // The cadence was 3 frames, which is the flash core's own lifetime rounded
    // UP: measured across settle 86/88/90/92/94, frame 90 landed in the trough
    // between two cores and photographed a dying flash (10k hot pixels against
    // 26-29k on either side). Two frames guarantees overlap.
    if (kind === 'fire') {
      const grab = Math.round(opts?.grabFrame ?? 90);
      const frames = [grab - 26, grab - 19, grab - 12];
      for (let f = grab - 6; f <= grab + 18; f += 2) frames.push(f);
      this._scriptFrames = frames.filter((f) => f >= 2);
    } else {
      this._scriptFrames = null;
    }
    return kind;
  }

  /** Swap without the draw animation (harness + debug only). */
  setWeaponImmediate(id) {
    if (!this.states.has(id)) return false;
    this._switchTo = null;
    this.activeId = id;
    this.viewmodel.setActive(id);
    return true;
  }

  _runDebug(ctx) {
    this._debugFrame = (this._debugFrame ?? 0) + 1;
    const frames = this._scriptFrames;
    if (!frames) return;
    for (const f of frames) {
      if (f === this._debugFrame) {
        this._fireTimer = 0;
        this.tryFire();
      }
    }
  }

  /* ====================================================================== */

  resize() {}

  dispose() {
    for (const off of this._off ?? []) off();
    this.sim?.clear();
    for (const t of this._liveThrowables) {
      this.ctx.scene.remove(t.mesh);
      if (this.physics?.removeRigidBody) this.physics.removeRigidBody(t.body);
    }
    this._liveThrowables.length = 0;
    for (const p of this._droppedMags) {
      p.group.removeFromParent();
      if (p.body && this.physics?.removeRigidBody) this.physics.removeRigidBody(p.body);
    }
    this._droppedMags.length = 0;
    this.viewmodel?.dispose();
    this.mats?.dispose();
  }
}
